import {
  Box,
  Collapse,
  IconButton,
  TableCell as MuiTableCell,
  TableHead as MuiTableHead,
  TableHeadProps as MuiTableHeadProps,
  Table,
  TableBody,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core"
import React, { FC, MouseEvent, useState } from "react"
import { useHistory } from "react-router-dom"
import { colors } from "../../utils/theme"
import { Order, StaffTableData } from "../../utils/types"
import ChevronDownIcon from "../atoms/ChevronDownIcon"
import ChevronUpIcon from "../atoms/ChevronUpIcon"
import TableCell from "../atoms/TableCell"

const CASEMIX_WIDTH = 388

const useStyles = makeStyles((theme) => ({
  table: {
    color: colors.neutral.denim,
    borderRadius: 0,
    boxShadow: "none",
    border: `1px solid ${colors.neutral.clay}`,
    backgroundColor: colors.neutral.white,
    width: "100%",
  },
  tableHeaderCell: {
    color: colors.neutral.denim,
    fontWeight: 700,
    borderBottom: `1px solid ${colors.neutral.clay}`,
    verticalAlign: "top",
  },
  tableSortLabel: {
    color: `${colors.neutral.denim} !important`,
    fontWeight: 700,
  },
  sublabel: {
    color: colors.neutral.denim_08,
    textAlign: "right",
    fontWeight: 400,
  },
  noData: {
    width: "100%",
    padding: 27,
    textAlign: "center",
    border: `1px solid ${colors.neutral.clay}`,
    borderTop: 0,
    backgroundColor: colors.neutral.white,
  },
  noDataFirstLine: {
    fontWeight: 700,
    marginBottom: 6,
  },
  pending: {
    color: colors.neutral.asphalt,
    fontStyle: "italic",
  },
  questionnaireColumn: {
    display: "flex",
    alignItems: "center",
  },
  questionnaireScore: {
    height: 33,
    width: 33,
    borderRadius: 4,
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  casemix: {
    height: 8,
    width: CASEMIX_WIDTH,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
  },
  casemixSection: {
    height: 8,
  },
  italic: {
    fontStyle: "italic",
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tableCell: {
    color: " #2F3B5A",
    cursor: "pointer",
    fontWeight: 400,
    borderBottom: "1px solid #C8CDDB",
    padding: theme.spacing(1.5),
  },
}))

interface Column {
  id: string
  label: string
  sublabel?: string
  align?: "right" | "left"
}

type TableHeadProps = {
  order: Order
  orderBy: string
  onRequestSort: (event: MouseEvent<unknown>, property: any) => void
} & MuiTableHeadProps

const TableHead: FC<TableHeadProps> = ({ order, orderBy, onRequestSort }) => {
  const classes = useStyles()
  const createSortHandler = (property: any) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }
  const columns: Column[] = [
    {
      id: "expand",
      label: "",
    },
    {
      id: "lastName",
      label: "Last Name",
      align: "left",
    },
    {
      id: "firstName",
      label: "First Name",
      align: "left",
    },
    {
      id: "displayName",
      label: "Display Name",
      align: "left",
    },
  ]
  return (
    <MuiTableHead>
      <TableRow>
        {columns.map((column) => (
          <MuiTableCell
            key={column.id}
            padding={column.label === "" ? "none" : undefined}
          >
            {column.label && (
              <Typography variant="body2" className={classes.tableSortLabel}>
                {column.label}
              </Typography>
            )}
          </MuiTableCell>
        ))}
      </TableRow>
    </MuiTableHead>
  )
}

const SupervisorTable: FC<{
  supervisorData?: StaffTableData[]
}> = ({ supervisorData }) => {
  const classes = useStyles()
  const [order, setOrder] = useState<Order>("asc")
  const [orderBy, setOrderBy] = useState<keyof StaffTableData>("lastName")
  const history = useHistory()
  const [activeId, setActiveId] = useState<number | null>(null)

  const handleRowClick = (id: number) => {
    setActiveId(activeId === id ? null : id)
  }

  return (
    <>
      <Table className={classes.table}>
        <TableHead
          order={order}
          orderBy={orderBy}
          onRequestSort={(event: React.MouseEvent<unknown>, property: any) => {
            const isAsc = orderBy === property && order === "asc"
            setOrder(isAsc ? "desc" : "asc")
            setOrderBy(property)
          }}
        />
        {supervisorData && supervisorData.length > 0 && (
          <TableBody>
            {supervisorData.map((data: any, index: number) => (
              <>
                <TableRow key={index}>
                  <TableCell className={classes.tableCell}>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => handleRowClick(data.supervisor.id)}
                    >
                      {activeId === data.supervisor.id ? (
                        <ChevronUpIcon />
                      ) : (
                        <ChevronDownIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div>
                      <Typography variant="body1">
                        {data.supervisor.last_name}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div className={classes.questionnaireColumn}>
                      <Typography variant="body1">
                        {data.supervisor.first_name}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div className={classes.questionnaireColumn}>
                      <Typography variant="body1">
                        {data.supervisor.display_name}
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow key={`${index}-actions`}>
                  <TableCell className={classes.tableHeaderCell} colSpan={12}>
                    <Collapse
                      in={activeId === data.supervisor.id}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box margin={4}>
                        <Typography variant="h6" gutterBottom component="div">
                          Supervisees
                        </Typography>
                        <Table>
                          <TableHead
                            order={"asc"}
                            orderBy={""}
                            onRequestSort={() => {}}
                          >
                            <TableRow>
                              <TableCell style={{ padding: 0 }}>
                                Last Name
                              </TableCell>
                              <TableCell style={{ padding: 0 }}>
                                First Name
                              </TableCell>
                              <TableCell style={{ padding: 0 }}>
                                Display Name
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {data.supervisee.map((row: any) => (
                              <TableRow
                                key={row.id}
                                onClick={() =>
                                  history.push(
                                    `/supervisee/${row.supervisee.id}`
                                  )
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <TableCell className={classes.tableCell}>
                                  {""}
                                </TableCell>
                                <TableCell className={classes.tableCell}>
                                  {row.lastName}
                                </TableCell>
                                <TableCell className={classes.tableCell}>
                                  {row.firstName}
                                </TableCell>
                                <TableCell className={classes.tableCell}>
                                  {row.displayName}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </>
            ))}
          </TableBody>
        )}
      </Table>

      {supervisorData && supervisorData.length === 0 && (
        <div className={classes.noData}>
          <Typography variant="body1" className={classes.noDataFirstLine}>
            You currently do not have any supervisor's listed on your dashboard.
          </Typography>
        </div>
      )}
    </>
  )
}

export default SupervisorTable
